import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  size: number;
  speedY: number;
  speedX: number;
  opacity: number;
  opacityDir: number;
  type: "heart" | "orb" | "spark";
  hue: number;
  rotation: number;
  rotationSpeed: number;
  pulsePhase: number;
  wobbleAmp: number;
  wobbleFreq: number;
}

export default function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let particles: Particle[] = [];
    let w = window.innerWidth;
    let h = window.innerHeight;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;
    };

    resize();
    window.addEventListener("resize", resize);

    // Count based on screen area, but keep it intimate - fewer, larger particles
    const area = w * h;
    const count = Math.min(Math.floor(area / 28000), 80);

    // Initialize particles
    for (let i = 0; i < count; i++) {
      const typeRand = Math.random();
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        size: typeRand < 0.3 ? 3 + Math.random() * 4 : typeRand < 0.65 ? 8 + Math.random() * 20 : 1.5 + Math.random() * 3,
        speedY: 0.08 + Math.random() * 0.25,
        speedX: (Math.random() - 0.5) * 0.15,
        opacity: 0.05 + Math.random() * 0.3,
        opacityDir: Math.random() > 0.5 ? 1 : -1,
        type: typeRand < 0.3 ? "spark" : typeRand < 0.65 ? "heart" : "orb",
        hue: 340 + Math.random() * 25, // Deep pink to crimson range
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.003,
        pulsePhase: Math.random() * Math.PI * 2,
        wobbleAmp: 0.3 + Math.random() * 1.2,
        wobbleFreq: 0.003 + Math.random() * 0.008,
      });
    }

    // Draw heart shape
    const drawHeart = (x: number, y: number, size: number, rotation: number) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      const s = size * 0.5;
      ctx.beginPath();
      const topCurveHeight = s * 0.3;
      ctx.moveTo(0, topCurveHeight);
      // Left curve
      ctx.bezierCurveTo(0, 0, -s, 0, -s, topCurveHeight);
      // Left bottom
      ctx.bezierCurveTo(-s, s * 0.7, s * -0.6, s * 0.95, 0, s * 1.2);
      // Right bottom
      ctx.bezierCurveTo(s * 0.6, s * 0.95, s, s * 0.7, s, topCurveHeight);
      // Right curve
      ctx.bezierCurveTo(s, 0, 0, 0, 0, topCurveHeight);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    // Draw four-pointed star spark
    const drawSpark = (x: number, y: number, size: number) => {
      ctx.save();
      ctx.translate(x, y);
      const half = size * 0.5;
      const quarter = size * 0.2;
      ctx.beginPath();
      ctx.moveTo(0, -half);
      ctx.lineTo(quarter, -quarter);
      ctx.lineTo(half, 0);
      ctx.lineTo(quarter, quarter);
      ctx.lineTo(0, half);
      ctx.lineTo(-quarter, quarter);
      ctx.lineTo(-half, 0);
      ctx.lineTo(-quarter, -quarter);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    const animate = () => {
      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Slow sine wobble horizontally
        p.x += Math.sin(p.pulsePhase) * p.wobbleAmp * 0.3;
        // Subtle drift
        p.x += p.speedX;

        // Float upward slowly (reverse gravity feels more dreamlike)
        p.y -= p.speedY;

        // Wrap around
        if (p.y < -p.size * 3) {
          p.y = h + p.size * 3;
          p.x = Math.random() * w;
        }
        if (p.y > h + p.size * 3) {
          p.y = -p.size * 3;
          p.x = Math.random() * w;
        }
        if (p.x < -p.size * 3) p.x = w + p.size * 3;
        if (p.x > w + p.size * 3) p.x = -p.size * 3;

        // Breathing opacity
        p.opacity += p.opacityDir * 0.0015;
        if (p.opacity >= 0.45) p.opacityDir = -1;
        if (p.opacity <= 0.03) p.opacityDir = 1;

        // Rotation
        p.rotation += p.rotationSpeed;
        p.pulsePhase += p.wobbleFreq;

        // Color: deep crimson to rose pink
        const saturation = p.type === "orb" ? 45 : p.type === "heart" ? 55 : 35;
        const lightness = p.type === "orb" ? 62 : p.type === "heart" ? 58 : 72;

        // Glow effect
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 2.5);
        gradient.addColorStop(0, `hsla(${p.hue}, ${saturation}%, ${lightness}%, ${p.opacity})`);
        gradient.addColorStop(0.4, `hsla(${p.hue}, ${saturation}%, ${lightness}%, ${p.opacity * 0.4})`);
        gradient.addColorStop(1, "transparent");

        ctx.fillStyle = gradient;

        if (p.type === "orb") {
          // Soft glowing circle
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.type === "heart") {
          // Heart with glow
          ctx.shadowColor = `hsla(${p.hue}, 60%, 55%, ${p.opacity * 0.7})`;
          ctx.shadowBlur = p.size * 0.8;
          drawHeart(p.x, p.y, p.size * 2, p.rotation);
          ctx.shadowBlur = 0;
        } else {
          // Sparkle
          ctx.shadowColor = `hsla(${p.hue}, 40%, 75%, ${p.opacity * 0.8})`;
          ctx.shadowBlur = p.size;
          drawSpark(p.x, p.y, p.size * 3);
          ctx.shadowBlur = 0;
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <>
      {/* Soft ambient radial glow spots */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `
            radial-gradient(ellipse 40% 35% at 25% 20%, hsla(345, 50%, 40%, 0.06) 0%, transparent 70%),
            radial-gradient(ellipse 35% 30% at 75% 60%, hsla(350, 45%, 38%, 0.05) 0%, transparent 70%),
            radial-gradient(ellipse 30% 25% at 50% 80%, hsla(0, 40%, 35%, 0.04) 0%, transparent 60%),
            radial-gradient(ellipse 25% 20% at 60% 15%, hsla(340, 40%, 42%, 0.05) 0%, transparent 60%)
          `,
        }}
      />
      {/* Vignette overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `
            radial-gradient(ellipse 80% 80% at 50% 50%, transparent 30%, hsla(345, 30%, 8%, 0.25) 100%)
          `,
        }}
      />
      {/* Canvas particles */}
      <canvas
        ref={canvasRef}
        className="pointer-events-none fixed inset-0 z-[1]"
        style={{ mixBlendMode: "screen" }}
      />
    </>
  );
}